import React, { useState } from "react";
import { Button } from "../../../../ui/components/Button";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import { FeatherDownload } from "@subframe/core";
import { FeatherFileSpreadsheet } from "@subframe/core";
import {
  downloadCustomersTemplate,
  downloadVendorsTemplate,
} from "../../data/api/customerVendorUploadApi";

interface TemplateDownloadSectionProps {
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
}

const TemplateDownloadSection: React.FC<TemplateDownloadSectionProps> = ({
  onError,
  onSuccess,
}) => {
  const [downloadingCustomers, setDownloadingCustomers] = useState(false);
  const [downloadingVendors, setDownloadingVendors] = useState(false);

  const handleDownloadCustomers = async () => {
    try {
      setDownloadingCustomers(true);
      await downloadCustomersTemplate();
    } catch (error: any) {
      const errorMessage =
        error.message || "Failed to download customers template";
      onError?.(errorMessage);
    } finally {
      setDownloadingCustomers(false);
    }
  };

  const handleDownloadVendors = async () => {
    try {
      setDownloadingVendors(true);
      await downloadVendorsTemplate();
    } catch (error: any) {
      const errorMessage =
        error.message || "Failed to download vendors template";
      onError?.(errorMessage);
    } finally {
      setDownloadingVendors(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <div className="flex w-full flex-col items-start gap-2">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Download Excel Templates
        </span>
        <span className="text-body font-body text-subtext-color">
          Download the Excel templates, fill them with your customer or vendor
          data, and upload them to bulk import into the system.
        </span>
      </div>

      <div className="flex w-full flex-col items-start gap-4">
        {/* Customers Template */}
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-default-background p-4">
          <div className="flex w-full items-center gap-3">
            <IconWithBackground
              size="medium"
              icon={<FeatherFileSpreadsheet />}
            />
            <div className="flex flex-1 flex-col items-start gap-1">
              <span className="text-body-bold font-body-bold text-default-font">
                Customers Template
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Download the Excel template for bulk customer import
              </span>
            </div>
            <Button
              icon={<FeatherDownload />}
              onClick={handleDownloadCustomers}
              disabled={downloadingCustomers}
            >
              {downloadingCustomers ? "Downloading..." : "Download"}
            </Button>
          </div>
        </div>

        {/* Vendors Template */}
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-default-background p-4">
          <div className="flex w-full items-center gap-3">
            <IconWithBackground
              size="medium"
              icon={<FeatherFileSpreadsheet />}
            />
            <div className="flex flex-1 flex-col items-start gap-1">
              <span className="text-body-bold font-body-bold text-default-font">
                Vendors Template
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Download the Excel template for bulk vendor import
              </span>
            </div>
            <Button
              icon={<FeatherDownload />}
              onClick={handleDownloadVendors}
              disabled={downloadingVendors}
            >
              {downloadingVendors ? "Downloading..." : "Download"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateDownloadSection;
